/**
 * Logic for computing prop/state-derived table measurements (heights, paddings).
 *
 * These helpers are intentionally free of DOM access so they can be memoized in
 * selectors. DOM-dependent measurements remain in the component.
 */

import {INDENT_SIZE} from 'notebook/notes/lists/ListConstants';
import {
  DEFAULT_MAXIMUM_FIXED_TABLE_HEIGHT,
  MaximumTableHeightType,
  TABLE_HEADER_HEIGHT,
  TABLE_ROW_HEIGHT,
} from 'notebook/tables/constants/TableLayoutConstants';

/**
 * The horizontal padding (in px) applied to the right of the table when it is
 * rendered inline within a document (i.e. not maximized/split screen and a
 * document name is present).
 */
export const PADDING_RIGHT_SIZE = 35;

/**
 * The numeric maximum height for the table. When an explicit numeric
 * `maximumHeight` is provided it is used directly; otherwise the default fixed
 * height is used. This does NOT account for whether the table is maximized or
 * intended to stretch to full height.
 */
export function computeMaxTableHeight(maximumHeight: MaximumTableHeightType | undefined): number {
  return typeof maximumHeight === 'number' ? maximumHeight : DEFAULT_MAXIMUM_FIXED_TABLE_HEIGHT;
}

/**
 * The minimum height that the `<table>` tag must be. The table may be taller
 * than this, but it should never be shorter. For a fixed-height table this is
 * capped at `maxTableHeight`; for a full-height table there is no cap.
 */
export function computeMinTableHeight(
  rowCount: number,
  columnHeaderHeight: number,
  isFullHeight: boolean,
  maxTableHeight: number
): number {
  const minFullHeight = rowCount * TABLE_ROW_HEIGHT + columnHeaderHeight;
  const effectiveMaxTableHeight = !isFullHeight ? maxTableHeight : undefined;
  if (effectiveMaxTableHeight) {
    return Math.min(minFullHeight, effectiveMaxTableHeight);
  }
  return minFullHeight;
}

/**
 * The height of the title bar, when visible. Returns 0 when the title bar is
 * suppressed. For non-virtualized tables the formula box is rendered together
 * with the title bar as a floating element, but that combined height is
 * computed at the component level because it depends on DOM measurement.
 */
export function computeTitleBarHeight(suppressTitleBar: boolean): number {
  return suppressTitleBar ? 0 : TABLE_HEADER_HEIGHT;
}

/**
 * The left indentation (in px) applied to the table. Maximized/split-screen
 * tables are never indented; otherwise the indentation scales with the table's
 * nesting depth.
 */
export function computeIndentationPx(isMaximizedOrSplitScreen: boolean, indentation: number): number {
  return isMaximizedOrSplitScreen ? 0 : indentation * INDENT_SIZE;
}

/**
 * The right padding (in px) applied to the table. Only inline tables within a
 * named document receive this padding; maximized/split-screen tables and tables
 * outside of a document do not.
 */
export function computePaddingPx(isMaximizedOrSplitScreen: boolean, hasDocumentName: boolean): number {
  return isMaximizedOrSplitScreen || !hasDocumentName ? 0 : PADDING_RIGHT_SIZE;
}
